import React, {Component, ReactNode} from 'react'
import {createResidentPage, ResidentPagePropsSet} from '../shared/hoc/createResidentPage'
import EventEmitterExResident from '../shared/pageBaseComponents/EventEmitterExResident'
import {RECEIVING_AGENCY_CD, RESIDENT_USER_TYPE} from '../../CodeMaster'
import {StyledPageTitle} from '../reservations/components/CommonStyle'
import {DisplayItemTitle} from '../../styledComponents/DisplayItem'
import {Colors, FontSize, FontWeights} from '../../styledComponents/Constants'
import {NatAsyncCallFunction} from '../../__NoAutomatedTests/common/util/Natutil'
import {Box} from '@mui/material'
import {BlockSpacer} from '../../styledComponents/ResponsiveConvenience'
import {FullWidthColumnFlexContainer} from '../../styledComponents/FlexComponents'
import {SessionStorageHelper} from '../../SessionStorageHelper'
import NatResidentUserRepo from '../../__NoAutomatedTests/components/residentUser/repository/NatResidentUserRepo'
import {Messages} from '../../Messages'
import {
    ResidentLoginInfoSessionStorageKey,
} from '../../__NoAutomatedTests/common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {ResidentAccountTransferContactUs} from './AccountTransferContactUs'
import {
    ApplyButton,
    BoldText,
    CompleteSampleArea,
    DescriptionArea,
    ListGridContainer,
    ListHeader,
    ListNumber,
    PcMarginLeft15,
    PcMarginLeft30,
    WhiteArea,
} from './AccountTransferCommon'
import {InlineLink} from '../../styledComponents/residentPage/Links'
import {faExternalLinkAlt} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'

interface Props {
    residentUserRepo: NatResidentUserRepo
    condominiumCd: string
    eventEmitter: EventEmitterExResident
}

interface State {
    isSubUser: boolean
    mainUserName: string
    isLoadCompleted: boolean
}

class ResidentAccountTransferApplyToSmbcPage extends Component<Props & ResidentPagePropsSet, State> {
    constructor(props: Readonly<Props & ResidentPagePropsSet>) {
        super(props)

        const userType = SessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_USER_RESIDENT_USER_TYPE)
        const isSubUser = userType === RESIDENT_USER_TYPE.SUB.value

        this.state = {
            isSubUser: isSubUser,
            mainUserName: '',
            isLoadCompleted: false,
        }
    }

    async componentDidMount(): Promise<void> {
        const {eventEmitter} = this.props
        const {isSubUser} = this.state

        await eventEmitter.showLoading()
        try {
            if (isSubUser) {
                await this.setupMainUserName()
            }
            this.setState({
                isLoadCompleted: true,
            })
        } catch {
            return
        } finally {
            eventEmitter.closeLoading()
        }
    }

    async setupMainUserName(): Promise<void> {
        const {residentUserRepo, navigate} = this.props
        const residentUserResponse = await NatAsyncCallFunction(
            residentUserRepo.getResidentUserLogined.bind(residentUserRepo),
            navigate,
        )
        if (!residentUserResponse) {
            throw Error('通信エラー')
        }

        if (!residentUserResponse.details || !residentUserResponse.details.mainResidentUserInfo) {
            this.props.navigate(`/resident/error`, {state: {message: Messages.ESYCC0014.toString()}})
            throw Error('サブユーザーでログイン時にメインユーザー情報がない')
        }
        const {familyName, firstName} = residentUserResponse.details.mainResidentUserInfo

        this.setState({
            mainUserName: `${familyName} ${firstName}`,
        })
    }

    render(): ReactNode {
        const {condominiumCd} = this.props
        const {isSubUser, mainUserName, isLoadCompleted} = this.state

        if (!isLoadCompleted) {
            return null
        }

        return <>
            <BlockSpacer height={'30px'}/>
            <StyledPageTitle>口座振替登録</StyledPageTitle>
            <PcMarginLeft15>
                <DescriptionArea>
                    {isSubUser && <>
                        <Box fontWeight={FontWeights.Bold} color={Colors.Alert}>
                            ご使用のアカウントはサブユーザのため、本機能をご利用いただけません。メインユーザの{mainUserName}さんにご依頼ください。
                        </Box>
                        <Box height={'20px'}/>
                    </>}
                    管理組合への毎月の支払い(管理費や施設利用料など)を、ご指定の口座より自動引落します。インターネット上で指定口座を登録・変更いただけます。
                    <Box height={'10px'}/>
                    <ListGridContainer fontSize={FontSize.Middle}>
                        <div>・</div>
                        <div>指定口座には、個人名義の普通預金口座をご利用いただけます。利用可能な金融機関は
                            <InlineLink
                                href={'https://www.smbc-card.com/business/collection/service/cats/bank_list_internet.jsp'}
                                target={'_blank'}
                                rel={'noopener noreferrer'}
                            >
                                三井住友カードサイト&thinsp;
                                <FontAwesomeIcon icon={faExternalLinkAlt}/>
                            </InlineLink>
                            &thinsp;からご確認ください。
                        </div>
                        <div>・</div>
                        <div>指定口座に信用金庫の口座を使用する場合は、口座名義のご本人様がお手続きをお願いいたします。</div>
                        <div>・</div>
                        <div>毎月10日までに登録すると、翌月から自動引落が開始されます。</div>
                        <div>・</div>
                        <div>
                            <BoldText>管理組合へ直接お支払いする費用がない方は、本手続きは不要です。</BoldText>(賃貸契約でお住いの方など)
                        </div>
                    </ListGridContainer>
                </DescriptionArea>
            </PcMarginLeft15>

            <WhiteArea>
                <DisplayItemTitle>お手続き手順</DisplayItemTitle>
                <PcMarginLeft30>
                    <Box height={'20px'}/>
                    お手続きには、三井住友カードおよび金融機関が提供する外部サイトを利用します。
                    <Box height={'30px'}/>
                    <ListGridContainer>
                        <ListNumber>1</ListNumber>
                        <ListHeader>お手続き開始</ListHeader>
                        <div></div>
                        <div>「お手続きを開始する」ボタンより、口座振替登録を開始します。</div>
                    </ListGridContainer>
                    <Box height={'20px'}/>
                    <ListGridContainer>
                        <ListNumber>2</ListNumber>
                        <ListHeader>個人情報保護方針への同意</ListHeader>
                        <div></div>
                        <div>個人情報保護方針への同意をします。</div>
                    </ListGridContainer>
                    <Box height={'20px'}/>
                    <ListGridContainer>
                        <ListNumber>3</ListNumber>
                        <ListHeader>口座情報入力</ListHeader>
                        <div></div>
                        <div>口座情報(金融機関、支店名、口座番号など)を入力します。</div>
                    </ListGridContainer>
                    <Box height={'20px'}/>
                    <ListGridContainer>
                        <ListNumber>4</ListNumber>
                        <ListHeader>本人確認</ListHeader>
                        <div></div>
                        <div>金融機関サイトにて、本人確認や認証を行います。</div>
                    </ListGridContainer>
                    <Box height={'20px'}/>
                    <ListGridContainer>
                        <ListNumber>5</ListNumber>
                        <ListHeader>お申し込み手続き結果の確認</ListHeader>
                        <div></div>
                        <div>三井住友カードサイトにて、お申し込み手続きが正常に終了していることを確認後、「素敵ネットに戻る」ボタンより、素敵ネットに移動します。</div>
                    </ListGridContainer>
                    <Box height={'20px'}/>
                    <ListGridContainer>
                        <ListNumber>6</ListNumber>
                        <ListHeader>お手続き完了</ListHeader>
                        <div></div>
                        <div>下の画面が表示されましたら、お手続き完了です。</div>
                    </ListGridContainer>
                    <Box height={'30px'}/>
                </PcMarginLeft30>
                <CompleteSampleArea>
                    <img src={'/resources/images/icons/icon_reserve_complete.svg'} width={'60px'}
                         alt={'complete'}/>
                    <Box height={'15px'}/>
                    <Box fontWeight={FontWeights.Bold} color={Colors.AccentDark}>お手続きが完了しました</Box>
                    <Box fontSize={FontSize.Small}>このページを閉じてください</Box>
                </CompleteSampleArea>
                <ApplyButton
                    disabled={isSubUser}
                    to={`/resident/condominiums/${condominiumCd}/applications/account-transfer/${RECEIVING_AGENCY_CD.SMBC_CARD.value}/privacy-policy`}>
                    <FullWidthColumnFlexContainer>
                        <div>
                            お手続きを開始する
                        </div>
                    </FullWidthColumnFlexContainer>
                </ApplyButton>
                <PcMarginLeft30>
                    <ListGridContainer fontSize={FontSize.Small}>
                        <div>・</div>
                        <div>公衆Wi-Fiでは利用しないでください。個人情報や入力情報が盗み見られる危険性があります。</div>
                        <div>・</div>
                        <div>三井住友カードサイトや金融機関サイトのシステムメンテナンスのため、一時的にご利用できない場合があります。</div>
                    </ListGridContainer>
                </PcMarginLeft30>
            </WhiteArea>
            <Box height={'20px'}/>
            <ResidentAccountTransferContactUs/>
        </>
    }
}

export default createResidentPage(ResidentAccountTransferApplyToSmbcPage)
